import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { resetPasswordSchema } from "../../utils/validationSchemas";
import LoadingSpinner from "../LoadingSpinner";
import "./AuthForms.css";

const ResetPasswordForm = ({ token, onSuccess }) => {
  const { isLoading, error, clearError, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
    mode: "onBlur",
  });

  const onSubmit = async (data) => {
    clearError();

    const result = await resetPassword(token, data.password);
    if (result.success) {
      console.log("Password reset successful:", result.data);
      
      // Check if user was automatically logged in
      if (result.data.token && result.data.user) {
        // User is now logged in, show success message briefly then redirect
        setResetComplete(true);
        
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        // Fallback to old behavior if no auto-login
        setResetComplete(true);
        if (onSuccess) {
          onSuccess(result.data);
        }
      }
    }
    // Error handling is managed by AuthContext
  };

  if (resetComplete) {
    return (
      <div className="auth-form">
        <div className="auth-header">
          <h2>Password Reset Successful!</h2>
          <p>Your password has been successfully updated and you've been logged in.</p>
        </div>

        <div className="success-message">
          <p>🎉 Welcome back! You're now logged in with your new password.</p>
          <p>Redirecting you home...</p>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <LoadingSpinner size="small" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-form">
      <div className="auth-header">
        <h2>Set New Password</h2>
        <p>Please enter your new password</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit(onSubmit)} className="form">
        <div className="form-group">
          <label htmlFor="password">New Password *</label>
          <div className="password-input-container">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              {...register("password")}
              className={errors.password ? "input-error" : ""}
              placeholder="New Password"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "👁️" : "👁️‍🗨️"}
            </button>
          </div>
          {errors.password && (
            <span className="field-error">{errors.password.message}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm New Password *</label>
          <div className="password-input-container">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              {...register("confirmPassword")}
              className={errors.confirmPassword ? "input-error" : ""}
              placeholder="Confirm New Password"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
            </button>
          </div>
          {errors.confirmPassword && (
            <span className="field-error">
              {errors.confirmPassword.message}
            </span>
          )}
        </div>

        <button
          type="submit"
          className="submit-button"
          disabled={isLoading || isSubmitting}
        >
          {isLoading || isSubmitting ? (
            <LoadingSpinner size="small" />
          ) : (
            "Update Password"
          )}
        </button>
      </form>
    </div>
  );
};

export default ResetPasswordForm;
